import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import { Container, Row, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  fetchBrokerOptions,
  fetchContractOptions,
  runReconciliation,
} from "./reconautomation.api";
import TradeDetailModal from "./TradeDetailModal";
import { BrokerOption } from "../../ApiSettings/ApiBrokerSettings";
import { DateFormatErrorModal, ValidationError } from "./dateFormateErrorModal";
import { exportReconCSV, ExportTab } from "./Reconexport.utils";
import BrowserUpdatedIcon from '@mui/icons-material/BrowserUpdated';
import { ACCEPTED_EXTENSIONS, ActiveTab, ActiveTabEnum, BreakReasonItem, BuySell, ReconRunResponse, ReconSourceEnum, ReconStatusEnum, ReconSummary, ReconTradeRow, SelectOption, SidedTableConfig } from "../../../Types/Reconautomation2.types";
import { useNavigate } from "react-router-dom";
import { useReconShared } from "../ReconCommon/ResharedContext";
import { ReconFilterActionsPanel } from "../ReconCommon/ReconFilterActionsPanel";
import { set } from "lodash";


const CONTRACT_ALL_OPTION: SelectOption = { label: "\u200B All", value: "" };

const DEFAULT_SOURCE = "Britannia" as const;

const STATUS_COLOR: Record<string, "success" | "error" | "warning" | "default"> = {
  MATCHED: "success",
  MISMATCH_PRICE: "error",
  MISMATCH_MARKUP: "warning",
  MISMATCH_PRICE_AND_MARKUP: "error",
  UNMATCHED_BGM: "warning",
  UNMATCHED_ACERS: "warning",
  CANON_FAILED: "default",
};

const STATUS_LABEL: Record<string, string> = {
  MATCHED: "Matched",
  MISMATCH_PRICE: "Price Mismatch",
  MISMATCH_MARKUP: "Markup Mismatch",
  MISMATCH_PRICE_AND_MARKUP: "Price & Markup",
  UNMATCHED_BGM: "Unmatched BGM",
  UNMATCHED_ACERS: "Unmatched ACERS",
  CANON_FAILED: "Canon Failed",
};

const PAIRED_STATUSES = new Set([
  ReconStatusEnum.MATCHED,
  "MISMATCH_PRICE",
  "MISMATCH_MARKUP", 
  "MISMATCH_PRICE_AND_MARKUP",
]);

const SUMMARY_CARDS: {
  key: keyof ReconSummary;
  label: string;
  accent: string;
}[] = [
    { key: "total_bgm_trades", label: "BGM Trades", accent: "#1565c0" },
    { key: "total_acers_trades", label: "ACERS Trades", accent: "#1565c0" },
    { key: "matched", label: "Matched", accent: "#2e7d32" },
    { key: "mismatch_price", label: "Price Mismatch", accent: "#c62828" },
    { key: "mismatch_markup", label: "Markup Mismatch", accent: "#e65100" },
    { key: "mismatch_price_and_markup", label: "Price & Markup", accent: "#c62828" },
    { key: "unmatched_bgm", label: "Unmatched BGM", accent: "#f57c00" },
    { key: "unmatched_acers", label: "Unmatched ACERS", accent: "#f57c00" },
    { key: "canon_failed", label: "Canon Failed", accent: "#555" },
  ];

const COMMON_TABLE_PROPS = {
  enableDensityToggle: false,
  enableColumnOrdering: true,
  enableColumnFilterModes: false,
  enableColumnPinning: true,
  initialState: { showColumnFilters: false, density: "compact" as const },
  muiTableHeadCellProps: { sx: { border: "1px solid #e0e0e087", fontStyle: "normal", fontWeight: "normal", fontSize: "0.72rem" } },
  muiTableBodyCellProps: { sx: { border: "1px solid #ebe9e98f", fontSize: "0.73rem" } },
  muiTablePaperProps: { elevation: 0, sx: { borderRadius: "0" } },
  muiTableContainerProps: { sx: { backgroundColor: "#fff" } },
  muiTopToolbarProps: {
    className: "top-toolbar-wrapper__new",
    sx: { backgroundColor: "#fff", padding: "8px" },
  },
  muiBottomToolbarProps: {
    className: "bottom-toolbar-wrapper__new",
    sx: { backgroundColor: "#fff", padding: "8px" },
  },

} as const;

const VIEW_TYPE_STORAGE_KEY = "recon_view_type";
type ViewType = "version-1" | "version-2";


const SummaryCard: React.FC<{ label: string; value: number; accent?: string }> = ({
  label,
  value,
  accent = "#00bcd4",
}) => (
  <Box
    sx={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", p: "10px 14px", borderRadius: "6px",
      border: `1px solid ${accent}40`, backgroundColor: `${accent}0d`,
      minWidth: 80, flex: "1 1 80px",
    }}
  >
    <Typography sx={{ fontSize: "1.35rem", fontWeight: 700, lineHeight: 1, color: accent }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "0.6rem", color: "#777", mt: 0.5, textAlign: "center", lineHeight: 1.2 }}>
      {label}
    </Typography>
  </Box>
);

const MismatchCell: React.FC<{
  bgm: number | null;
  acers: number | null;
  highlight: boolean;
}> = ({ bgm, acers, highlight }) => (
  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
    {(["BGM", "ACERS"] as const).map((src) => {
      const val = src === "BGM" ? bgm : acers;
      const hilit = src === "ACERS" && highlight;
      return (
        <Box key={src}>
          <Typography sx={{ fontSize: "0.58rem", color: "#aaa" }}>{src}</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: hilit ? "#c62828" : "inherit" }}>
            {val ?? "—"} {hilit && "🔴"}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <Chip
    label={STATUS_LABEL[status] ?? status}
    color={STATUS_COLOR[status] ?? "default"}
    size="small"
    sx={{ fontSize: "0.62rem", height: 20 }}
  />
);

const BreakReasonCell: React.FC<{ value: ReconTradeRow["break_reason"]; showRow?: boolean }> = ({ value, showRow = false }) => {
  if (!value) return null;

  if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
    const items = value as BreakReasonItem[];
    return (
      <Typography sx={{ fontSize: "0.72rem", color: "#c62828" }}>
        {items.map(item => showRow ? `Row ${item.row}: ${item.reason}` : item.reason).join(", ")}
      </Typography>
    );
  }
  return (
    <Typography sx={{ fontSize: "0.72rem", color: "#c62828" }}>
      {Array.isArray(value) ? value.join(", ") : value}
    </Typography>
  );
};

const normalizeStatus = (status?: string | null) =>
  (status ?? "").trim().toUpperCase();

const splitCanonicalisationStatuses = (status?: string | null) =>
  normalizeStatus(status)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const CANONICALISATION_STATUS_LABELS: Record<string, string> = {
  UNSUPPORTED_DATE_FORMAT_TRADE_DATE: "Unsupported Trade Date Format",
  UNSUPPORTED_DATE_FORMAT_CONTRACT_MONTH: "Unsupported Contract Month Format",
  INVALID_DIRECTION: "Invalid Direction",
  FAILED_MISSING_CLIENT_MAPPING: "Client Mapping Failed",
  FAILED_MISSING_CONTRACT_MAPPING: "Contract Mapping Failed",
  FAILED_MISSING_CLIENT_MAPPING_AND_CONTRACT_MAPPING: "Client & Contract Mapping Failed",
  INVALID_QUANTITY: "Invalid Quantity",
};

const formatCanonicalisationStatus = (status?: string | null) => {
  const statuses = splitCanonicalisationStatuses(status);
  if (!statuses.length) return "—";

  return statuses.map((item) => CANONICALISATION_STATUS_LABELS[item] ?? item).join(", ");
};

const isBreakReasonItem = (item: unknown): item is BreakReasonItem => {
  if (!item || typeof item !== "object") return false;

  const candidate = item as Partial<BreakReasonItem>;
  return (
    typeof candidate.row === "number" &&
    typeof candidate.value === "string" &&
    typeof candidate.reason === "string"
  );
};

const getBreakReasonItems = (value: ReconTradeRow["break_reason"]): BreakReasonItem[] => {
  if (!Array.isArray(value)) return [];

  const items: unknown[] = value;
  return items.filter(isBreakReasonItem);
};

const getFailureFieldFallback = (
  row: ReconTradeRow,
  field: "trade_date" | "contract_month" | "direction" | "client" | "contract" | "quantity"
): string | number | null => {
  const statuses = splitCanonicalisationStatuses(row.canonicalisation_status);
  const reasons = getBreakReasonItems(row.break_reason);

  if (
    field === "trade_date" &&
    statuses.includes("UNSUPPORTED_DATE_FORMAT_TRADE_DATE")
  ) {
    return reasons.find((item) => item.reason === "Unsupported Date Format For Trade Date.Use DD-MM-YYYY or DD-MM-YY")?.value ?? null;
  }

  if (
    field === "contract_month" &&
    statuses.includes("UNSUPPORTED_DATE_FORMAT_CONTRACT_MONTH")
  ) {
    return reasons.find((item) => item.reason === "Unsupported Date Format for Contract Month.Use DD-MM-YYYY or DD-MM-YY")?.value ?? null;
  }

  if (
    field === "client" &&
    (
      statuses.includes("FAILED_MISSING_CLIENT_MAPPING_AND_CONTRACT_MAPPING") ||
      statuses.includes("FAILED_MISSING_CLIENT_MAPPING")
    )
  ) {
    return reasons.find((item) => item.reason === "Customer mapping failed")?.value ?? null;
  }

  if (
    field === "contract" &&
    (
      statuses.includes("FAILED_MISSING_CLIENT_MAPPING_AND_CONTRACT_MAPPING") ||
      statuses.includes("FAILED_MISSING_CONTRACT_MAPPING")
    )
  ) {
    return reasons.find((item) => item.reason === "Contract mapping failed")?.value ?? null;
  }

  if (field === "quantity" && statuses.includes("INVALID_QUANTITY")) {
    return reasons.find((item) => item.reason === "Invalid quantity")?.value ?? null;
  }

  if (field === "direction" && statuses.includes("INVALID_DIRECTION")) {
    return reasons.find((item) => item.reason === "Invalid direction")?.value ?? null;
  }

  return null;
};


/** Reusable info-icon cell that opens the detail modal */
const makeDetailColumn = (
  onOpen: (row: ReconTradeRow) => void
): MRT_ColumnDef<ReconTradeRow> => ({
  id: "detail",
  header: "",
  size: 30,
  minSize: 30,
  maxSize: 30,
  enableResizing: false,
  enableSorting: false,
  enableColumnActions: false,
  enableColumnDragging: false,
  enableColumnOrdering: false,
  enableHiding: false,
  enablePinning: false,
  muiTableHeadCellProps: { sx: { padding: "0 4px" } },
  muiTableBodyCellProps: { sx: { padding: "0 4px" } },
  Cell: ({ row }) => (
    <InfoOutlinedIcon
      sx={{ fontSize: 16, color: "#00bcd4", cursor: "pointer" }}
      onClick={() => onOpen(row.original)}
    />
  ),
});

const makeMismatchColumn = (
  field: "price" | "markup"
): MRT_ColumnDef<ReconTradeRow> => ({
  id: field,
  header: field === "price" ? "Price" : "Markup",
  size: 160,
  Cell: ({ row }) => (
    <MismatchCell
      bgm={field === "price" ? row.original.bgm_price : row.original.markup_bgm}
      acers={field === "price" ? row.original.acers_price : row.original.markup_acers}
      highlight={
        row.original.reconciliation_status === (field === "price" ? "MISMATCH_PRICE" : "MISMATCH_MARKUP") ||
        row.original.reconciliation_status === "MISMATCH_PRICE_AND_MARKUP"
      }
    />
  ),
});


const ReconciliationAutomationPage2: React.FC = () => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [sourceOptions, setSourceOptions] = useState<SelectOption[]>([]);
  const [contractOptions, setContractOptions] = useState<SelectOption[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [summary, setSummary] = useState<ReconSummary | null>(null);
  const [results, setResults] = useState<ReconTradeRow[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>("all");
  const [unmatchedSubTab, setUnmatchedSubTab] = useState<"bgm" | "acers">("bgm");
  const [modalTrade, setModalTrade] = useState<ReconTradeRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formatErrors, setFormatErrors] = useState<ValidationError[] | null>(null);

  const navigate = useNavigate();
  const {
    filters,
    updateFilter,
    selectedFile,
    setSelectedFile,
    fileInputRef,
    resetAll,
    autoRunOnMount,
    setAutoRunOnMount,
    version2Cache,
    setVersion2Cache,
  } = useReconShared();

  const [viewType, setViewType] = useState<ViewType>(() => {
    const stored = localStorage.getItem(VIEW_TYPE_STORAGE_KEY);
    return stored === "version-1" || stored === "version-2"
      ? stored
      : "version-1";
  });

  // ── Callbacks ──────────────────────────────────────────────────────────────

  const handleViewTypeChange = useCallback(
    (newViewType: ViewType) => {
      setViewType(newViewType);
      localStorage.setItem(VIEW_TYPE_STORAGE_KEY, newViewType);

      if (selectedFile) setAutoRunOnMount(true);

      if (newViewType === "version-1") {
        navigate(`${process.env.PUBLIC_URL}/data-table/reconciliation-automation-version-1`);
      } else {
        navigate(`${process.env.PUBLIC_URL}/data-table/reconciliation-automation-version-2`);
      }
    },
    [navigate, selectedFile, setAutoRunOnMount]
  );

  const openModal = useCallback((trade: ReconTradeRow) => {
    setModalTrade(trade);
    setModalOpen(true);
  }, []);

  const handleClear = useCallback(() => {
    resetAll();
    setSummary(null);
    setResults([]);
    setHasRun(false);
    setRunId(null);
  }, [resetAll]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file format "${ext}". Please upload .xlsx, .xlsm, or .csv only.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
  }, [fileInputRef, setSelectedFile]);

  const applyResults = useCallback((data: ReconRunResponse) => {
    const r = data.results;
    const computedSummary: ReconSummary = {
      total_bgm_trades: r.filter(x => x.source_system !== ReconSourceEnum.ACERS).length,
      total_acers_trades: r.filter(x => x.source_system === ReconSourceEnum.ACERS).length,
      matched: r.filter(x => x.reconciliation_status === "MATCHED").length,
      mismatch_price: r.filter(x => x.reconciliation_status === "MISMATCH_PRICE").length,
      mismatch_markup: r.filter(x => x.reconciliation_status === "MISMATCH_MARKUP").length,
      mismatch_price_and_markup: r.filter(x => x.reconciliation_status === "MISMATCH_PRICE_AND_MARKUP").length,
      unmatched_bgm: r.filter(x => x.reconciliation_status === "UNMATCHED_BGM").length,
      unmatched_acers: r.filter(x => x.reconciliation_status === "UNMATCHED_ACERS").length,
      canon_failed: r.filter(x => x.canonicalisation_status !== "SUCCESS").length,
    };
    setSummary(computedSummary);
    setResults(data.results);
    setRunId(data.run_id);
    setHasRun(true);
    setActiveTab("all");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setVersion2Cache({
      summary: computedSummary,
      results: data.results,
      runId: data.run_id,
      fileName: selectedFile?.name ?? null,
    });
  }, [fileInputRef,selectedFile,setVersion2Cache]);

  const handleRun = useCallback(async () => {
    if (!filters.start_date || !filters.end_date) return toast.error("Please select a date range.");
    if (!filters.source) return toast.error("Please select a source.");
    if (!selectedFile) return toast.error("Please upload a BGM broker file.");
    setIsRunning(true);
    try {
         const data = await runReconciliation({
           source: filters.source,
           start_date: filters.start_date,
           end_date: filters.end_date,
           file: selectedFile,
           ...(filters.contract && { contract: filters.contract }),
           ...(filters.buy_sell && { buy_sell: filters.buy_sell as BuySell }),
         });
      applyResults(data);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Reconciliation completed successfully!");
    } catch (err: any) {
      const details = err?.response?.data?.details;
      if (details?.status === "partial" && details?.errors?.length) {
        setFormatErrors(details.errors);
      } else {
        toast.error("Reconciliation failed. Please try again.");
        console.error(err);
      }
    } finally {
      setIsRunning(false);
    }
  }
  , [filters, selectedFile, applyResults, fileInputRef]);

  useEffect(() => {
    if (version2Cache) {
      setSummary(version2Cache.summary as ReconSummary);
      setResults(version2Cache.results as ReconTradeRow[]);
      setRunId(version2Cache.runId);
      setHasRun(true);
      setActiveTab("all");
      return;
    }
    if (autoRunOnMount && selectedFile) {
      setAutoRunOnMount(false);
      handleRun();
    }
  }, [version2Cache, autoRunOnMount, selectedFile, handleRun]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const source = e.currentTarget;

    const containers = document.querySelectorAll<HTMLDivElement>(".paired-table-container");
    containers.forEach(el => {
      if (el !== source) {
        el.scrollLeft = source.scrollLeft;
        el.scrollTop = source.scrollTop;
      }
    });
  }, []);


  useEffect(() => {
    fetchBrokerOptions().then(({ data, error }) => {
      if (error || !Array.isArray(data)) {
        toast.error(`Error loading sources: ${error ?? "Unknown error"}`);
        setSourceOptions([]);
      } else {
        const opts = data.map((item: BrokerOption) => ({ value: item.name, label: item.name }));
        setSourceOptions(opts);
        if (!filters.source) updateFilter("source", DEFAULT_SOURCE);
      }
    });

    fetchContractOptions()
      .then((opts) => {
        setContractOptions([CONTRACT_ALL_OPTION, ...opts]);
      })
      .catch(() => toast.error("Error loading contract options"));
  }, []);
  const handleDownloadTemplate = () => {
    const a = document.createElement("a");
    a.href = "/templates/recon-template.csv";
    a.download = "recon-template.csv";
    a.click();
  };

  const rows = useMemo(() => ({
    all: results,
    matched: results.filter((r) => r.reconciliation_status === "MATCHED"),
    mismatch: results.filter((r) => ["MISMATCH_PRICE", "MISMATCH_MARKUP", "MISMATCH_PRICE_AND_MARKUP"].includes(r.reconciliation_status)),
    unmatchedBgm: results.filter((r) => r.reconciliation_status === "UNMATCHED_BGM"),
    unmatchedAcers: results.filter((r) => r.reconciliation_status === "UNMATCHED_ACERS"),
    failures: results.filter((r) => r.canonicalisation_status !== "SUCCESS"),
  }), [results]);


  const detailCol = useMemo(() => makeDetailColumn(openModal), [openModal]);

  const columns = useMemo(() => {
    const base: MRT_ColumnDef<ReconTradeRow>[] = [
      { accessorKey: "trade_date", header: "Trade Date", size: 90 },
      { accessorKey: "source_trade_id", header: "Trade ID", size: 100 },
      { accessorKey: "client", header: "Client", size: 80 },
      { accessorKey: "contract", header: "Contract", size: 90 },
      { accessorKey: "quantity", header: "Quantity", size: 60 },
    ];

    const contractMonthCol: MRT_ColumnDef<ReconTradeRow> = {
      accessorKey: "contract_month",
      header: "Contract Month",
      size: 90,
    };


    const statusCol: MRT_ColumnDef<ReconTradeRow> = {
      accessorKey: "reconciliation_status",
      header: "Status",
      size: 140,
      Cell: ({ cell }) => <StatusChip status={cell.getValue<string>()} />,
    };

    const breakCol: MRT_ColumnDef<ReconTradeRow> = {
      accessorKey: "break_reason",
      header: "Break Reason",
      size: 260,
      Cell: ({ cell }) => <BreakReasonCell value={cell.getValue<ReconTradeRow["break_reason"]>()} />,
    };

    const failureBase: MRT_ColumnDef<ReconTradeRow>[] = base.map((col) => {
      if (col.accessorKey === "trade_date") {
        return {
          ...col,
          Cell: ({ row }) => (
            <Typography sx={{ fontSize: "0.72rem" }}>
              {row.original.trade_date || getFailureFieldFallback(row.original, "trade_date") || "—"}
            </Typography>
          ),
        } as MRT_ColumnDef<ReconTradeRow>;
      }

      if (col.accessorKey === "client") {
        return {
          ...col,
          Cell: ({ row }) => {
            return (
              <Typography sx={{ fontSize: "0.72rem" }}>
                {row.original.client || getFailureFieldFallback(row.original, "client") || "—"}
              </Typography>
            );
          },
        } as MRT_ColumnDef<ReconTradeRow>;
      }

      if (col.accessorKey === "contract") {
        return {
          ...col,
          Cell: ({ row }) => {
            return (
              <Typography sx={{ fontSize: "0.72rem" }}>
                {row.original.contract || getFailureFieldFallback(row.original, "contract") || "—"}
              </Typography>
            );
          },
        } as MRT_ColumnDef<ReconTradeRow>;
      }

      if (col.accessorKey === "quantity") {
        return {
          ...col,
          Cell: ({ row }) => (
            <Typography sx={{ fontSize: "0.72rem" }}>
              {row.original.quantity ?? getFailureFieldFallback(row.original, "quantity") ?? "—"}
            </Typography>
          ),
        } as MRT_ColumnDef<ReconTradeRow>;
      }

      return col;
    });

    failureBase.push(
      {
        ...contractMonthCol,
        Cell: ({ row }) => (
          <Typography sx={{ fontSize: "0.72rem" }}>
            {row.original.contract_month || getFailureFieldFallback(row.original, "contract_month") || "—"}
          </Typography>
        ),
      } as MRT_ColumnDef<ReconTradeRow>,
      {
        accessorKey: "direction",
        header: "Direction",
        size: 90,
        Cell: ({ row }) => (
          <Typography sx={{ fontSize: "0.72rem" }}>
            {row.original.direction || getFailureFieldFallback(row.original, "direction") || "—"}
          </Typography>
        ),
      } as MRT_ColumnDef<ReconTradeRow>,
    );

    return {
      all: [
         detailCol,
        { accessorKey: "trade_date", header: "Trade Date", size: 90 },
        { accessorKey: "source_system", header: "Source", size: 70 },
        { accessorKey: "source_trade_id", header: "Trade ID", size: 100 },
        { accessorKey: "client", header: "Client", size: 80 },
        { accessorKey: "contract", header: "Contract", size: 90 },
        { accessorKey: "contract_month", header: "Contract Month", size: 90 },
        { accessorKey: "quantity", header: "Quantity", size: 60 },
        { accessorKey: "price", header: "Price", size: 100 },
        { accessorKey: "bgm_price", header: "Price (BGM)", size: 100 },
        { accessorKey: "acers_price", header: "Price (ACERS)", size: 110 },
        statusCol,
        { accessorKey: "matched_with_id", header: "Matched With ID", size: 110 },
        { accessorKey: "direction", header: "Direction", size: 150 },
        breakCol,
      ] as MRT_ColumnDef<ReconTradeRow>[],

      matched: [
        ...base,
        { accessorKey: "bgm_price", header: "Price (BGM)", size: 100 },
        { accessorKey: "acers_price", header: "Price (ACERS)", size: 110 },
        statusCol,
      ] as MRT_ColumnDef<ReconTradeRow>[],

      mismatch: [
        ...base,
        makeMismatchColumn("price"),
        makeMismatchColumn("markup"),
        statusCol,
        breakCol,
        detailCol,
      ] as MRT_ColumnDef<ReconTradeRow>[],

      unmatched: [
        ...base,
        contractMonthCol,
        statusCol,
        breakCol,
      ] as MRT_ColumnDef<ReconTradeRow>[],

      failures: [
        ...failureBase,
        {
          accessorKey: "break_reason",
          header: "Uploaded File Row",
          size: 70,
          Cell: ({ cell }) => {
            const val = cell.getValue<ReconTradeRow["break_reason"]>();
            if (!val || !Array.isArray(val) || typeof val[0] !== "object") {
              return <Typography sx={{ fontSize: "0.72rem" }}>—</Typography>;
            }

            const rows = Array.from(new Set((val as BreakReasonItem[]).map((item) => item.row)));
            return <Typography sx={{ fontSize: "0.72rem", color: "#555" }}>{rows.join(", ")}</Typography>;
          },
        },
        { accessorKey: "source_system", header: "Source", size: 80 },
        {
          accessorKey: "canonicalisation_status",
          header: "Canon Status",
          size: 200,
          Cell: ({ cell }) => (
            <Typography sx={{ fontSize: "0.72rem", color: "#c62828", fontWeight: 600 }}>
              {cell.getValue<string>() ?? "—"}
            </Typography>
          ),
        },
        {
          accessorKey: "break_reason",
          header: "Issue",
          size: 260,
          Cell: ({ cell }) => <BreakReasonCell value={cell.getValue<ReconTradeRow["break_reason"]>()} />,
        },
      ] as MRT_ColumnDef<ReconTradeRow>[],
    };
  }, [detailCol]);


  const unmatchedBgmTable = useMaterialReactTable({
    ...COMMON_TABLE_PROPS, columns: columns.unmatched, data: rows.unmatchedBgm, getRowId: (r) => r.id,
    renderTopToolbarCustomActions: () => (
      <Button
        onClick={() => exportReconCSV("unmatched_bgm", rows.unmatchedBgm, runId)}
        startIcon={<BrowserUpdatedIcon />}
      >
        CSV
      </Button>
    ),
  });
  const unmatchedAcersTable = useMaterialReactTable({
    ...COMMON_TABLE_PROPS, columns: columns.unmatched, data: rows.unmatchedAcers, getRowId: (r) => r.id,
    renderTopToolbarCustomActions: () => (
      <Button
        onClick={() => exportReconCSV("unmatched_acers", rows.unmatchedAcers, runId)}
        startIcon={<BrowserUpdatedIcon />}
      >
        CSV
      </Button>
    ),
  });
  const failureTable = useMaterialReactTable({
    ...COMMON_TABLE_PROPS, columns: columns.failures, data: rows.failures, getRowId: (r) => r.id,
    renderTopToolbarCustomActions: () => (
      <Button
        onClick={() => exportReconCSV("failures", rows.failures, runId)}
        startIcon={<BrowserUpdatedIcon />}
      >
        CSV
      </Button>
    ),
  });
  const tabLabel = useCallback(
    (label: string, count: number) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {label}
        {hasRun && (
          <Box sx={{ fontSize: "0.62rem", px: 0.7, py: 0.1, borderRadius: "10px", backgroundColor: "#00bcd420", color: "#007b8c", fontWeight: 700, lineHeight: 1.6 }}>
            {count}
          </Box>
        )}
      </Box>
    ),
    [hasRun]
  );


  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div className="table-ad">

            {/* ── Filters & Actions ── */}
            <Accordion defaultActiveKey="0" className="table-ad__addInputs">
              <Accordion.Item eventKey="0">
                <Accordion.Header className="accordion_button">
                  Reconciliation — Filters & Actions
                </Accordion.Header>
                <Accordion.Body>
                  <ReconFilterActionsPanel
                    filters={filters}
                    updateFilter={updateFilter}
                    sourceOptions={sourceOptions}
                    contractOptions={contractOptions}
                    viewType={viewType}
                    onViewTypeChange={handleViewTypeChange}
                    selectedFile={selectedFile}
                    fileInputRef={fileInputRef}
                    onFileChange={handleFileChange}
                    isRunning={isRunning}
                    hasRun={hasRun}
                    onRun={handleRun}
                    onClear={handleClear}
                    onDownloadTemplate={handleDownloadTemplate}
                    footerHint={
                      hasRun ? (
                        <Typography sx={{ fontSize: "0.65rem", color: "#00897b", mt: 1 }}>
                          ✓ File in session — Re-run will reuse the uploaded file without re-uploading.
                        </Typography>
                      ) : null
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* ── Summary ── */}
            {summary && (
              <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, mb: 1 }}>
                  Summary
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {SUMMARY_CARDS.map(({ key, label, accent }) => (
                    <SummaryCard key={key} label={label} value={summary[key] as number} accent={accent} />
                  ))}
                </Box>
              </Box>
            )}

            {/* ── Results (Tabs) ── */}
            {hasRun && (
              <Box sx={{ mt: 1.5, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{ borderBottom: "1px solid #e0e0e0", minHeight: 36, "& .MuiTab-root": { minHeight: 36, fontSize: "0.72rem", textTransform: "none", py: 0 } }}
                >
                  <Tab value={ActiveTabEnum.ALL} label={tabLabel("All", rows.all.length)} />
                  <Tab value={ActiveTabEnum.MATCHED} label={tabLabel("Matched", rows.matched.length)} />
                  <Tab value={ActiveTabEnum.MISMATCHES} label={tabLabel("Mismatches", rows.mismatch.length)} />
                  <Tab value={ActiveTabEnum.UNMATCHED} label={tabLabel("Unmatched", rows.unmatchedBgm.length + rows.unmatchedAcers.length)} />
                  <Tab value={ActiveTabEnum.FAILURES} label={tabLabel("Failures", rows.failures.length)} />
                </Tabs>

                {(activeTab === ActiveTabEnum.ALL || activeTab === ActiveTabEnum.MATCHED || activeTab === ActiveTabEnum.MISMATCHES) && (() => {
                  const activeData =
                    activeTab === ActiveTabEnum.ALL ? results :
                      activeTab === ActiveTabEnum.MATCHED ? rows.matched :
                        rows.mismatch;

                  type PairedRow = { acers: ReconTradeRow | null; bgm: ReconTradeRow | null };

                  const acersAll = activeData.filter(r => r.source_system === ReconSourceEnum.ACERS);
                  const bgmAll = activeData.filter(r => r.source_system !== ReconSourceEnum.ACERS);


                  const matchedPairs: PairedRow[] = acersAll
                    .filter(a => PAIRED_STATUSES.has(a.reconciliation_status) && a.source_trade_id)
                    .map(a => ({
                      acers: a,
                      bgm: bgmAll.find(b => b.matched_with_id === a.source_trade_id) ?? null,
                    }));

                  const pairedAcersIds = new Set(matchedPairs.map(p => p.acers?.source_trade_id).filter(Boolean));
                  const pairedBgmIds = new Set(matchedPairs.map(p => p.bgm?.matched_with_id).filter(Boolean));

                  const unmatchedAcersPairs: PairedRow[] = acersAll
                    .filter(a => !pairedAcersIds.has(a.source_trade_id))
                    .map(a => ({ acers: a, bgm: null }));

                  const unmatchedBgmPairs: PairedRow[] = bgmAll
                    .filter(b => !pairedBgmIds.has(b.matched_with_id))
                    .map(b => ({ acers: null, bgm: b }));

                  const pairedRows: PairedRow[] = [
                    ...matchedPairs.filter(p =>
                      p.acers?.reconciliation_status?.startsWith("MISMATCH") ||
                      p.bgm?.reconciliation_status?.startsWith("MISMATCH")
                    ),
                    ...unmatchedAcersPairs,
                    ...unmatchedBgmPairs,
                    ...matchedPairs.filter(p =>
                      !p.acers?.reconciliation_status?.startsWith("MISMATCH") &&
                      !p.bgm?.reconciliation_status?.startsWith("MISMATCH")
                    ),
                  ];

                  // ── Shared column builders ──
                  const directionCell = (getValue: () => string) => (
                    <Typography sx={{
                      fontSize: "0.72rem", fontWeight: 700,
                      color: getValue() === "B" ? "#1565c0" : "#c62828"
                    }}>
                      {getValue()}
                    </Typography>
                  );

                  const emptyCell = <Typography sx={{ fontSize: "0.72rem", color: "#ccc" }}>—</Typography>;

                  const makeCols = (side: "acers" | "bgm"): MRT_ColumnDef<PairedRow>[] => {
                    const get = (row: PairedRow): ReconTradeRow | null => row[side];

                    const col = (
                      key: keyof ReconTradeRow,
                      header: string,
                      size: number,
                      renderFn?: (val: any, row: ReconTradeRow) => React.ReactNode
                    ): MRT_ColumnDef<PairedRow> => ({
                      id: `${side}_${key}`,
                      header,
                      size,
                      accessorFn: (row) => get(row)?.[key] ?? null,
                      Cell: ({ row }) => {
                        const r = get(row.original);
                        if (!r) return emptyCell;
                        const val = r[key];
                        return renderFn
                          ? <>{renderFn(val, r)}</>
                          : <Typography sx={{ fontSize: "0.73rem", color: "var(--color-text-primary)" }}>
                            {typeof val === "object" && val !== null
                              ? Array.isArray(val)
                                ? (val as any[]).map(v => typeof v === "object" ? v.reason : String(v)).join(", ")
                                : JSON.stringify(val)
                              : (val ?? "—")}
                          </Typography>;
                      },
                    });

                    const firstCol: MRT_ColumnDef<PairedRow> = side === "acers"
                      ? col("source_trade_id", "Trade ID", 140)
                      : col("matched_with_id", "Matched With ID", 150);

                    return [
                      {
                        id: `${side}_detail`,
                        header: "",
                        size: 30, minSize: 30, maxSize: 30,
                        enableResizing: false, enableSorting: false,
                        enableColumnActions: false, enableColumnDragging: false,
                        enableColumnOrdering: false, enableHiding: false, enablePinning: false,
                        muiTableHeadCellProps: { sx: { padding: "0 4px" } },
                        muiTableBodyCellProps: {
                          sx: {
                            height: "40px",
                            py: 0,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          },
                        },
                        Cell: ({ row }) => {
                          const r = get(row.original);
                          if (!r) return null;
                          return (
                            <InfoOutlinedIcon
                              sx={{ fontSize: 16, color: "#00bcd4", cursor: "pointer" }}
                              onClick={() => openModal(r)}
                            />
                          );
                        },
                      },
                      firstCol,
                      col("trade_date", "Trade Date", 90),
                      col("source_system", "Source", 70),
                      col("client", "Client", 140),
                      col("contract", "Contract", 70),
                      col("contract_month", "Contract Month", 90),
                      col("direction", "Dir", 50, (val) => directionCell(() => val ?? "")),
                      col("quantity", "Qty", 55),
                      col("price", "Price", 80),
                      col("acers_price", "ACERS Price", 90),
                      col("bgm_price", "BGM Price", 90),
                      col("reconciliation_status", "Status", 140, (val) => <StatusChip status={val} />),
                      col("break_reason", "Break Reason", 160, (val) => <BreakReasonCell value={val} />),
                    ];
                  };

                  const SIDE_CONFIGS: Record<"acers" | "bgm", SidedTableConfig> = {
                    acers: { label: "ACERS Trades", accent: "#1565c0", bg: "#e3f2fd", border: "#90caf9", systemFilter: (r) => r.source_system === "ACERS" },
                    bgm: { label: "BGM Trades", accent: "#2e7d32", bg: "#e8f5e9", border: "#a5d6a7", systemFilter: (r) => r.source_system !== "ACERS" },
                  };

                  const PairedSideTable: React.FC<{
                    config: SidedTableConfig;
                    side: "acers" | "bgm";
                    pairedRows: PairedRow[];
                  }> = ({ config, side, pairedRows }) => {

                    const isAcers = side === "acers";
                    const sideFilter = (r: ReconTradeRow) =>
                      isAcers ? r.source_system === ReconSourceEnum.ACERS : r.source_system !== ReconSourceEnum.ACERS;

                    const exportTab =
                      activeTab === ActiveTabEnum.MATCHED
                        ? "matched"
                        : activeTab === ActiveTabEnum.MISMATCHES
                          ? "mismatch"
                          : "matched";

                    const exportRows =
                      activeTab === ActiveTabEnum.MATCHED
                        ? rows.matched.filter(sideFilter)
                        : activeTab === ActiveTabEnum.MISMATCHES
                          ? rows.mismatch.filter(sideFilter)
                          : results.filter(sideFilter);

                    const exportTabForSide: ExportTab =
                      activeTab === ActiveTabEnum.ALL
                        ? side === "acers"
                          ? "all_acers"
                          : "all_bgm"
                        : exportTab as ExportTab;

                    const table = useMaterialReactTable<PairedRow>({
                      ...COMMON_TABLE_PROPS,
                      columns: makeCols(side),
                      data: pairedRows,
                      getRowId: (_, i) => String(i),
                      renderTopToolbarCustomActions: () => (
                        <Button
                          onClick={() => exportReconCSV(exportTabForSide, exportRows, runId)}
                          startIcon={<BrowserUpdatedIcon />}
                        >
                          CSV
                        </Button>
                      ),
                      enablePagination: true,
                      enableStickyHeader: true,
                      initialState: {
                        density: "compact",
                        showColumnFilters: false,
                        columnPinning: { left: [`${side}_detail`] },
                        pagination: { pageSize: 100, pageIndex: 0 },
                      },
                      muiTableHeadProps: {
                        sx: {
                          zIndex: 1,
                        },
                      },
                      muiTableHeadCellProps: {
                        sx: {
                          zIndex: 1,
                        },
                      },
                      muiTablePaperProps: { elevation: 0, sx: { borderRadius: 0 } },
                      muiTableContainerProps: { sx: { maxHeight: "400px", overflowY: "auto" },
                      className: "paired-table-container",
                       onScroll: handleScroll,
                    },
                      muiTableBodyRowProps: ({ row }) => {
                        const isMismatch =
                          row.original.acers?.reconciliation_status?.startsWith("MISMATCH") ||
                          row.original.bgm?.reconciliation_status?.startsWith("MISMATCH");

                        const getMismatchColor = () => {
                          if (side === "acers") return "#fff3e0";
                          if (side === "bgm") return "#fce4ec";
                        };

                        return {
                          sx: {
                            backgroundColor: isMismatch
                              ? getMismatchColor()
                              : row.original.acers && row.original.bgm
                                ? "#f1f8f1"
                                : !row.original.bgm
                                  ? "#f0f4ff"
                                  : "#fff3f3",
                            height: "30px",
                            minHeight: "30px",
                            maxHeight: "30px",
                          },
                        };
                      },
                      muiTopToolbarProps: {
                        className: "top-toolbar-wrapper__new",
                        sx: { backgroundColor: "#fff", padding: "8px" },
                      },
                      muiBottomToolbarProps: {
                        className: "bottom-toolbar-wrapper__new",
                        sx: { backgroundColor: "#fff", padding: "8px" },
                      },
                    });

                    const tradeCount = pairedRows.filter(p => !!p[side]).length;

                    return (
                      <Box sx={{ flex: 1, minWidth: 0, border: "1px solid #e0e0e0", borderRadius: "4px", overflow: "hidden" }}>
                        <Box sx={{ px: 2, py: 1, background: config.bg, borderBottom: `1px solid ${config.border}` }}>
                          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: config.accent, textTransform: "uppercase", letterSpacing: 1 }}>
                            {config.label} — {tradeCount} trades
                          </Typography>
                        </Box>
                        <MaterialReactTable table={table} />
                      </Box>
                    );
                  };

                  return (
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid #e0e0e0", gap: "8px", p: "8px" }}>
                      {(["acers", "bgm"] as const).map(side => (
                        <PairedSideTable
                          key={side}
                          config={SIDE_CONFIGS[side]}
                          side={side}
                          pairedRows={pairedRows}
                        />
                      ))}
                    </Box>
                  );
                })()}

                {/* ── Unmatched — pill toggle, one table at a time ── */}
                {activeTab === "unmatched" && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}>
                      {(["bgm", "acers"] as const).map((key) => {
                        const count = key === "bgm" ? rows.unmatchedBgm.length : rows.unmatchedAcers.length;
                        const active = unmatchedSubTab === key;
                        const color = key === "bgm" ? "#e65100" : "#1565c0";
                        return (
                          <Box
                            key={key}
                            onClick={() => setUnmatchedSubTab(key)}
                            sx={{
                              cursor: "pointer", px: 1.5, py: 0.4, borderRadius: "20px",
                              fontSize: "0.7rem", fontWeight: active ? 700 : 400,
                              backgroundColor: active ? color : "#f5f5f5",
                              color: active ? "#fff" : "#555",
                              border: `1px solid ${active ? color : "#ddd"}`,
                              transition: "all 0.15s", userSelect: "none",
                              display: "flex", alignItems: "center", gap: 0.5,
                            }}
                          >
                            {key === "bgm" ? "Unmatched BGM" : "Unmatched ACERS"}
                            <Box sx={{ fontSize: "0.6rem", px: 0.6, py: 0.1, borderRadius: "10px", backgroundColor: active ? "rgba(255,255,255,0.25)" : "#e0e0e0", color: active ? "#fff" : "#777", fontWeight: 700, lineHeight: 1.6 }}>
                              {count}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    {unmatchedSubTab === "bgm" && <MaterialReactTable table={unmatchedBgmTable} />}
                    {unmatchedSubTab === "acers" && <MaterialReactTable table={unmatchedAcersTable} />}
                  </Box>
                )}

                {/* ── Failures ── */}
                {activeTab === "failures" && (
                  <Box>
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                      <Typography sx={{ fontSize: "0.65rem", color: "#e65100" }}>
                        These trades could not be canonicalised and are excluded from reconciliation results until the mapping issue is resolved.
                      </Typography>
                    </Box>
                    <MaterialReactTable table={failureTable} />
                  </Box>
                )}
              </Box>
            )}

          </div>
        </Row>
      </Container>
      {formatErrors && (
        <DateFormatErrorModal
          errors={formatErrors}
          onClose={() => setFormatErrors(null)}
        />
      )}
      <TradeDetailModal open={modalOpen} onClose={() => setModalOpen(false)} trade={modalTrade} />
    </div>
  );
};

export default ReconciliationAutomationPage2;